/**
 * 学习正文本地缓存（设计文档 §3：第 1 级加密缓存，纯前端）
 * - 介质：IndexedDB（ziwei-learn / chapters，key=chapterId）——浏览器/WebView 内置本地库，数据不出设备
 * - 付费正文 AES-GCM 256 加密：密钥 = HKDF(SHA-256, IKM=token, salt=JWT user_id, info=ziwei-learn-cache-v1)，仅存内存不落盘；
 *   token 失效/换账号 → 旧付费条目自然密码学不可读（解密失败删除回源），无需主动清缓存
 * - 免费正文明文存储（无加密价值，省 CPU）
 * - 失效纯被动三件套：密钥随 token 失效 + 读时权益复核删除（§3.3）+ 目录 version 比对覆盖写（§3.1）
 * - 任何一步失败都静默降级为"未命中回源"，缓存层永不阻塞阅读
 */
import type { LearnChapterSummary } from './learn';

const DB_NAME = 'ziwei-learn';
const STORE = 'chapters';
const DB_VERSION = 1;

/** 缓存条目：enc=true 付费密文（iv+cipher base64）；否则明文 content */
interface CacheEntry {
  id: number;
  version: number;
  effective_level: string;
  enc?: boolean;
  iv?: string;
  cipher?: string;
  content?: string;
  updatedAt: number;
}

/** 读缓存上下文：token 派生密钥用；memberLevel 权益复核用（阶段三 /membership/me 上线前为 null=跳过读时复核，靠 403 批量兜底） */
export interface LearnCacheCtx {
  token: string | null;
  memberLevel?: string | null;
}

// ---------- IndexedDB 轻封装（手写，不引库） ----------

let dbPromise: Promise<IDBDatabase> | null = null;

function openDb(): Promise<IDBDatabase> {
  if (!dbPromise) {
    dbPromise = new Promise((resolve, reject) => {
      const req = indexedDB.open(DB_NAME, DB_VERSION);
      req.onupgradeneeded = () => {
        if (!req.result.objectStoreNames.contains(STORE)) {
          req.result.createObjectStore(STORE, { keyPath: 'id' });
        }
      };
      req.onsuccess = () => resolve(req.result);
      req.onerror = () => reject(req.error ?? new Error('IndexedDB 打开失败'));
    });
  }
  return dbPromise;
}

function idbGet(id: number): Promise<CacheEntry | undefined> {
  return openDb().then(
    (db) =>
      new Promise((resolve, reject) => {
        const req = db.transaction(STORE, 'readonly').objectStore(STORE).get(id);
        req.onsuccess = () => resolve(req.result as CacheEntry | undefined);
        req.onerror = () => reject(req.error);
      }),
  );
}

function idbPut(entry: CacheEntry): Promise<unknown> {
  return openDb().then(
    (db) =>
      new Promise((resolve, reject) => {
        const req = db.transaction(STORE, 'readwrite').objectStore(STORE).put(entry);
        req.onsuccess = () => resolve(null);
        req.onerror = () => reject(req.error);
      }),
  );
}

function idbDelete(id: number): Promise<unknown> {
  return openDb().then(
    (db) =>
      new Promise((resolve, reject) => {
        const req = db.transaction(STORE, 'readwrite').objectStore(STORE).delete(id);
        req.onsuccess = () => resolve(null);
        req.onerror = () => reject(req.error);
      }),
  );
}

function idbAll(): Promise<CacheEntry[]> {
  return openDb().then(
    (db) =>
      new Promise((resolve, reject) => {
        const req = db.transaction(STORE, 'readonly').objectStore(STORE).getAll();
        req.onsuccess = () => resolve((req.result ?? []) as CacheEntry[]);
        req.onerror = () => reject(req.error);
      }),
  );
}

// ---------- 密钥（HKDF 派生自登录 token，仅内存） ----------

let keyCache: { token: string; key: CryptoKey | null } | null = null;

/** WebCrypto 可用性：非 https/非安全上下文不可用 → 缓存整体退化明文（§3.2 降级） */
function cryptoAvailable(): boolean {
  return typeof crypto !== 'undefined' && !!crypto.subtle;
}

/** 从 JWT payload 解 user_id 作 HKDF salt——仅取盐值不做鉴权（不验签无安全含义；token 被换导致 salt 变化仅使旧缓存解密失败回源，无害） */
function saltFromToken(token: string): string {
  try {
    const b64 = token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/');
    const payload = JSON.parse(atob(b64)) as { user_id?: number | string };
    return String(payload.user_id ?? 'anon');
  } catch {
    return 'anon';
  }
}

async function deriveKey(token: string): Promise<CryptoKey | null> {
  if (keyCache?.token === token) return keyCache.key;
  let key: CryptoKey | null = null;
  if (cryptoAvailable()) {
    try {
      const base = await crypto.subtle.importKey('raw', new TextEncoder().encode(token), 'HKDF', false, ['deriveKey']);
      key = await crypto.subtle.deriveKey(
        {
          name: 'HKDF',
          hash: 'SHA-256',
          salt: new TextEncoder().encode(saltFromToken(token)),
          info: new TextEncoder().encode('ziwei-learn-cache-v1'),
        },
        base,
        { name: 'AES-GCM', length: 256 },
        false,
        ['encrypt', 'decrypt'],
      );
    } catch (e) {
      console.warn('[learnCache] 密钥派生失败，付费缓存退化明文：', e);
    }
  } else {
    console.warn('[learnCache] crypto.subtle 不可用（非安全上下文），付费缓存退化明文');
  }
  keyCache = { token, key };
  return key;
}

// ---------- base64 <-> bytes ----------

function bytesToB64(buf: ArrayBuffer | Uint8Array): string {
  const u8 = buf instanceof Uint8Array ? buf : new Uint8Array(buf);
  let s = '';
  for (let i = 0; i < u8.length; i++) s += String.fromCharCode(u8[i]);
  return btoa(s);
}

function b64ToBytes(b64: string): Uint8Array<ArrayBuffer> {
  const s = atob(b64);
  const buf = new ArrayBuffer(s.length);
  const u8 = new Uint8Array(buf);
  for (let i = 0; i < s.length; i++) u8[i] = s.charCodeAt(i);
  return u8;
}

// ---------- 权益复核（§3.3） ----------

const LEVEL_RANK: Record<string, number> = { free: 0, basic: 1, pro: 2 };

/** 当前上下文能否读该等级正文。memberLevel=null（阶段一过渡，me 接口未上线）→ 放行，靠 403 批量失效兜底 */
function levelAllowed(entryLevel: string, ctx: LearnCacheCtx): boolean {
  if (!entryLevel || entryLevel === 'free') return true;
  if (ctx.memberLevel == null) return true;
  // basic 现阶段全员开放（§1.4 收费开关未切），等级比较沿用服务端同序
  return (LEVEL_RANK[ctx.memberLevel] ?? 0) >= (LEVEL_RANK[entryLevel] ?? 0);
}

// ---------- 对外 API ----------

/**
 * 读缓存：版本不符/权益不足（删）/解密失败（删）均返回 null（调用方回源）。
 * 命中且复核通过返回正文明文。
 */
export async function getCachedChapter(id: number, expectVersion: number, ctx: LearnCacheCtx): Promise<string | null> {
  try {
    const entry = await idbGet(id);
    if (!entry || entry.version !== expectVersion) return null;
    if (!levelAllowed(entry.effective_level ?? '', ctx)) {
      await idbDelete(id).catch(() => undefined);
      return null;
    }
    if (!entry.enc) return entry.content ?? null;
    const key = ctx.token ? await deriveKey(ctx.token) : null;
    if (!key) return null; // 付费密文但无密钥可用 → 回源（退化环境已另行明文写入）
    try {
      const plain = await crypto.subtle.decrypt({ name: 'AES-GCM', iv: b64ToBytes(entry.iv ?? '') }, key, b64ToBytes(entry.cipher ?? ''));
      return new TextDecoder().decode(plain);
    } catch {
      // 解密失败（token 已更换/refresh 后未重派生/数据损坏）→ 删条目，下次回源重写（自愈，无需显式重加密迁移）
      await idbDelete(id).catch(() => undefined);
      return null;
    }
  } catch {
    return null; // 缓存层整体不可用：静默回源
  }
}

/** 写缓存：付费正文 AES-GCM 加密（有密钥时）；免费或无密钥退化环境明文 */
export async function putCachedChapter(
  summary: Pick<LearnChapterSummary, 'id' | 'version' | 'effective_level'>,
  content: string,
  ctx: LearnCacheCtx,
): Promise<void> {
  try {
    const paid = !!summary.effective_level && summary.effective_level !== 'free';
    const key = paid && ctx.token ? await deriveKey(ctx.token) : null;
    const entry: CacheEntry = {
      id: summary.id,
      version: summary.version,
      effective_level: summary.effective_level,
      updatedAt: Date.now(),
    };
    if (key) {
      const iv = crypto.getRandomValues(new Uint8Array(12));
      const ct = await crypto.subtle.encrypt({ name: 'AES-GCM', iv }, key, new TextEncoder().encode(content));
      entry.enc = true;
      entry.iv = bytesToB64(iv);
      entry.cipher = bytesToB64(ct);
    } else {
      entry.content = content;
    }
    await idbPut(entry);
  } catch {
    // 写失败不影响本次展示
  }
}

/** 删除单章缓存（403 锁态时该章数据已不可信） */
export async function deleteCachedChapter(id: number): Promise<void> {
  await idbDelete(id).catch(() => undefined);
}

/** §3.3-2 批量兜底：详情接口意外 403（等级刚过期/降级）→ 清除全部付费条目（免费保留） */
export async function purgePaidChapters(): Promise<void> {
  try {
    const all = await idbAll();
    await Promise.all(all.filter((e) => e.enc || (e.effective_level && e.effective_level !== 'free')).map((e) => idbDelete(e.id)));
  } catch {
    // 忽略
  }
}

/**
 * 一次性清理历史明文缓存（§0 现状：升级前 LearnPage 把正文——含付费正文——明文存 localStorage）。
 * 带 token 的付费正文不落盘残留，存量泄漏点从此关闭。
 */
export function purgeLegacyPlainCache(): void {
  try {
    const doomed: string[] = [];
    for (let i = 0; i < localStorage.length; i++) {
      const k = localStorage.key(i);
      if (k?.startsWith('paipan.learn.chapter.')) doomed.push(k);
    }
    doomed.forEach((k) => localStorage.removeItem(k));
  } catch {
    // 隐私模式等场景静默
  }
}
